import React, { useMemo, useState } from "react";
import "./SIPCalculator.css";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";

ChartJS.register(ArcElement, Tooltip, Legend);

const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(
    Number(Math.round(n || 0))
  );

 const YEARS_MIN = 1;
 const YEARS_MAX = 30;
 const RATE_MIN = 6;
 const RATE_MAX = 30;
 const ANNUAL_INCREASE_MIN = 0;
 const ANNUAL_INCREASE_MAX = 100;

export default function SIPCalculator({onBack}) {
  const [amount, setAmount] = useState(10000);
  const [rate, setRate] = useState(12);
  const [years, setYears] = useState(10);
  const [annualIncrease, setAnnualIncrease] = useState(10);

  const results = useMemo(() => {
    const baseAmount = Number(amount) || 0;
    const safeYears = Number(years) || 0;
    const safeRate = Number(rate) || 0;
    const safeAnnualIncrease = Number(annualIncrease) || 0;

    const monthlyRate = safeRate / 100 / 12;
    let futureValue = 0;
    let totalInvested = 0;

    for (let year = 0; year < safeYears; year++) {
      const monthlyInvestment =
        baseAmount * Math.pow(1 + safeAnnualIncrease / 100, year);

      for (let month = 0; month < 12; month++) {
        const monthsRemaining = (safeYears - year) * 12 - month;
        const fv = monthlyInvestment * Math.pow(1 + monthlyRate, monthsRemaining);
        futureValue += fv;
        totalInvested += monthlyInvestment;
      }
    }

    const returns = futureValue - totalInvested;
    return {
      invested: totalInvested,
      returns,
      future_value: futureValue,
    };
  }, [amount, annualIncrease, rate, years]);

  const rangeBg = (value, min, max) => {
    const pct = ((value - min) / (max - min)) * 100;
    return `linear-gradient(90deg, #6e2cff ${pct}%, rgba(255,255,255,0.04) ${pct}%)`;
  };

  const chartData = {
    labels: ["Invested Amount", "Est. Returns"],
    datasets: [
      {
        data: [results.invested, results.returns],
        backgroundColor: ["#ff8c2a", "#3d5afe"],
        borderWidth: 0,
      },
    ],
  };

  return (
    <div className="sip-page">
        {/* Header */}
      <header className="oil-header">
        <div className="oil-header-container">
          <div className="logo">
          <img src="images/Final Font Stocks4U1.png" className="logo-text" />
          </div>
        </div>
      </header>
      <div className="sip-inner container">
        <div className="s4u-title-row">
          <div className="s4u-page-heading">
            <button className="oil-back-btn s4u-back-btn" onClick={onBack}>
              ← Back to Dashboard
            </button>
            <h1 className="sip-title">SIP Calculator</h1>
          </div>
        </div>
        <p className="sip-desc">
          The SIP calculator helps estimate the potential growth of your
          Systematic Investment Plan (SIP) investment over your chosen time
          frame. SIP is a convenient method to save for your long-term financial
          goals.
        </p>

        <button className="sip-btn">SIP Investment Amount</button>

        <div className="sip-grid">
          {/* LEFT CARD */}
          <div className="sip-card left">
            <h3 className="card-heading">Returns Estimator</h3>
            <p className="subtext">
              Estimation is based on the past performance
            </p>

            <div className="amount-box">
              <input
                type="number"
                className="amount-input"
                value={amount}
                onChange={(e) => setAmount(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="Enter Amount"
              />
            </div>

            <div className="control">
              <label className="control-label">
                Select Duration
              </label>
              <input
                type="range"
                min={YEARS_MIN}
                max={YEARS_MAX}
                value={years}
                onChange={(e) => setYears(Number(e.target.value))}
                style={{ background: rangeBg(years, YEARS_MIN, YEARS_MAX) }}
              />
              <div className="minmax">
                <span>{years} yr</span>
              </div>
            </div>

            <div className="control">
              <label className="control-label">
                Expected Rate Of Return
              </label>
              <input
                type="range"
                min={RATE_MIN}
                max={RATE_MAX}
                step="0.5"
                value={rate}
                onChange={(e) => setRate(Number(e.target.value))}
                style={{ background: rangeBg(rate, RATE_MIN, RATE_MAX) }}
              />
              <div className="minmax">
                <span>{rate}%</span>
              </div>
            </div>

            <div className="control">
              <label className="control-label">
                Annual Increase
              </label>
              <input
                type="range"
                min={ANNUAL_INCREASE_MIN}
                max={ANNUAL_INCREASE_MAX}
                step="1"
                value={annualIncrease}
                onChange={(e) => setAnnualIncrease(Number(e.target.value))}
                style={{
                  background: rangeBg(
                    annualIncrease,
                    ANNUAL_INCREASE_MIN,
                    ANNUAL_INCREASE_MAX
                  ),
                }}
              />
              <div className="minmax">
                <span>{annualIncrease}%</span>
              </div>
            </div>
          </div>

          {/* RIGHT CARD */}
          <div className="sip-card right">
            <div className="result-title">
              The Total Value Of Your Investment After{" "}
              <span className="highlight">{years} Years</span> Will Be
            </div>
            <div className="result-big">₹ {formatINR(results.future_value)}</div>

            <div className="chart-box">
              <Doughnut
                data={chartData}
                options={{
                  cutout: "70%",
                  plugins: { legend: { position: "right", labels: { color: "#fff" } } },
                }}
              />
            </div>

            <div className="legend">
              <div className="legend-item">
                <span
                  className="color-dot"
                  style={{ background: "#ff8c2a" }}
                ></span>
                Invested Amount {formatINR(results.invested)}
              </div>
              <div className="legend-item">
                <span
                  className="color-dot"
                  style={{ background: "#3d5afe" }}
                ></span>
                Est. Returns {formatINR(results.returns)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
